/**
 * VideoCallWidget - Meeting Creation & View Widget
 *
 * Pattern: DetailPanel (side panel, mobile = full screen)
 *
 * CREATE MODE: Form to create a new video call meeting
 * VIEW MODE: Shows meeting details with Start/Join/Copy Link buttons
 */

import { useState, useCallback } from 'react';
import {
  Video,
  Calendar,
  Clock,
  Users,
  Link2,
  Copy,
  Check,
  Play,
  ExternalLink,
} from 'lucide-react';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks';
import { useVideoCallStore, type VideoCallMeeting } from '../../stores/videoCallStore';

// =============================================================================
// TYPES
// =============================================================================

interface VideoCallWidgetProps {
  isOpen?: boolean;
  meetingId?: string;
  meeting?: VideoCallMeeting;
  contactId?: string;
  onClose: () => void;
  onSave?: (meeting: VideoCallMeeting) => void;
  onJoinCall?: (meeting: VideoCallMeeting) => void;
}

// =============================================================================
// VIEW MODE
// =============================================================================

function MeetingViewMode({
  meeting,
  onClose,
  onJoinCall,
}: {
  meeting: VideoCallMeeting;
  onClose: () => void;
  onJoinCall?: (meeting: VideoCallMeeting) => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [linkCopied, setLinkCopied] = useState(false);
  const { joinRoom, startMeeting } = useVideoCallStore();

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCopyLink = async () => {
    const link = meeting.guestLink || `https://ba.teknology.eu/meet/${meeting.slug}`;
    await navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleStartOrJoin = async () => {
    if (meeting.status === 'scheduled') {
      await startMeeting(meeting.slug);
    }
    if (onJoinCall) {
      onJoinCall(meeting);
    } else {
      await joinRoom(meeting.name);
    }
  };

  const getStatusBadge = () => {
    switch (meeting.status) {
      case 'active':
        return { label: t('videocall.meetingActive'), color: '#22c55e', bg: '#dcfce7' };
      case 'scheduled':
        return { label: t('videocall.meetingScheduled'), color: '#3b82f6', bg: '#dbeafe' };
      case 'ended':
        return { label: t('videocall.meetingEnded'), color: '#6b7280', bg: '#f3f4f6' };
    }
  };

  const badge = getStatusBadge();

  return (
    <DetailPanel
      title={meeting.title}
      subtitle={meeting.status === 'active' ? t('videocall.meetingActive') : undefined}
      icon={<Video size={18} style={{ color: '#7C3AED' }} />}
      onClose={onClose}
      accentColor="#7C3AED"
      width="50%"
    >
      {/* Status Badge */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '9999px',
        background: badge.bg,
        color: badge.color,
        fontSize: '13px',
        fontWeight: 600,
        marginBottom: '20px',
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: badge.color,
          animation: meeting.status === 'active' ? 'pulse 2s infinite' : undefined,
        }} />
        {badge.label}
      </div>

      {/* Meeting Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        {meeting.scheduledAt && (
          <div style={statCardStyle}>
            <Calendar size={18} style={{ color: '#6b7280' }} />
            <div>
              <div style={statLabelStyle}>{t('videocall.scheduledFor')}</div>
              <div style={statValueStyle}>{formatDate(meeting.scheduledAt)}</div>
            </div>
          </div>
        )}
        {meeting.duration && (
          <div style={statCardStyle}>
            <Clock size={18} style={{ color: '#6b7280' }} />
            <div>
              <div style={statLabelStyle}>{t('videocall.duration')}</div>
              <div style={statValueStyle}>{meeting.duration} min</div>
            </div>
          </div>
        )}
      </div>

      {/* Participants */}
      {meeting.participants.length > 0 && (
        <DetailSection title={t('videocall.participants')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {meeting.participants.map((p, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: 'var(--bg, #f9fafb)',
                borderRadius: '8px',
              }}>
                <Users size={14} style={{ color: '#7C3AED' }} />
                <span style={{ fontSize: '14px' }}>{p}</span>
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      {/* Guest Link */}
      {meeting.guestAccessEnabled && (
        <DetailSection title={t('videocall.guestLink')}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 12px',
            background: '#f3e8ff',
            borderRadius: '8px',
            border: '1px solid #e9d5ff',
          }}>
            <Link2 size={16} style={{ color: '#7C3AED', flexShrink: 0 }} />
            <span style={{
              flex: 1,
              fontSize: '13px',
              color: '#7C3AED',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {meeting.guestLink || `https://ba.teknology.eu/meet/${meeting.slug}`}
            </span>
            <button
              onClick={handleCopyLink}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                border: 'none',
                borderRadius: '6px',
                background: '#7C3AED',
                color: 'white',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {linkCopied ? <Check size={14} /> : <Copy size={14} />}
              {linkCopied ? t('videocall.linkCopied') : t('videocall.copyGuestLink')}
            </button>
          </div>
        </DetailSection>
      )}

      {/* Actions */}
      <DetailActions>
        <button onClick={onClose} style={closeBtnStyle}>
          {t('common.close')}
        </button>
        {meeting.status !== 'ended' && (
          <button onClick={handleStartOrJoin} style={startBtnStyle}>
            <Play size={16} />
            {meeting.status === 'active' ? t('videocall.joinCall') : t('videocall.startMeeting')}
          </button>
        )}
      </DetailActions>
    </DetailPanel>
  );
}

// =============================================================================
// CREATE MODE
// =============================================================================

function MeetingCreateMode({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave?: (meeting: VideoCallMeeting) => void;
}) {
  const { t } = useTranslation();
  const { createMeeting } = useVideoCallStore();

  const [title, setTitle] = useState('');
  const [scheduledAt, setScheduledAt] = useState(
    new Date().toISOString().slice(0, 16),
  );
  const [duration, setDuration] = useState<number | ''>('');
  const [participantsInput, setParticipantsInput] = useState('');
  const [guestAccess, setGuestAccess] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState<VideoCallMeeting | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setIsSubmitting(true);

    try {
      const participants = participantsInput
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);

      const meeting = await createMeeting({
        title: title.trim(),
        scheduledAt: scheduledAt || null,
        duration: duration || null,
        participants,
        guestAccessEnabled: guestAccess,
      });

      setCreatedMeeting(meeting);
      onSave?.(meeting);
    } catch (err) {
      console.error('Failed to create meeting:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!createdMeeting) return;
    const link = createdMeeting.guestLink || `https://ba.teknology.eu/meet/${createdMeeting.slug}`;
    await navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Show success state after creation
  if (createdMeeting) {
    return (
      <DetailPanel
        title={t('videocall.meetingScheduled')}
        icon={<Check size={18} style={{ color: '#22c55e' }} />}
        onClose={onClose}
        accentColor="#22c55e"
        width="50%"
      >
        <div style={{
          textAlign: 'center',
          padding: '20px 0',
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: '#dcfce7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Video size={28} style={{ color: '#22c55e' }} />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600 }}>
            {createdMeeting.title}
          </h3>
          {createdMeeting.scheduledAt && (
            <p style={{ margin: '0 0 20px', color: 'var(--text-muted)', fontSize: '14px' }}>
              {new Date(createdMeeting.scheduledAt).toLocaleString()}
            </p>
          )}
        </div>

        {createdMeeting.guestAccessEnabled && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: '#f3e8ff',
            borderRadius: '10px',
            marginBottom: '16px',
          }}>
            <Link2 size={16} style={{ color: '#7C3AED', flexShrink: 0 }} />
            <span style={{
              flex: 1,
              fontSize: '13px',
              color: '#7C3AED',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {createdMeeting.guestLink || `https://ba.teknology.eu/meet/${createdMeeting.slug}`}
            </span>
            <button onClick={handleCopyLink} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              border: 'none',
              borderRadius: '6px',
              background: '#7C3AED',
              color: 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
              flexShrink: 0,
            }}>
              {linkCopied ? <Check size={14} /> : <Copy size={14} />}
              {linkCopied ? t('videocall.linkCopied') : t('videocall.copyGuestLink')}
            </button>
          </div>
        )}

        <DetailActions>
          <button onClick={onClose} style={closeBtnStyle}>
            {t('common.close')}
          </button>
          <button
            onClick={() => {
              const { joinRoom } = useVideoCallStore.getState();
              joinRoom(createdMeeting.name);
              onClose();
            }}
            style={startBtnStyle}
          >
            <Play size={16} />
            {t('videocall.startNow')}
          </button>
        </DetailActions>
      </DetailPanel>
    );
  }

  return (
    <DetailPanel
      onClose={onClose}
      title={t('videocall.newMeeting')}
      subtitle={t('videocall.scheduleMeeting')}
      icon={<Video size={18} style={{ color: '#7C3AED' }} />}
      accentColor="#7C3AED"
      width="50%"
      footer={
        <DetailActions>
          <button style={closeBtnStyle} onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            style={{
              ...startBtnStyle,
              opacity: !title.trim() || isSubmitting ? 0.5 : 1,
              cursor: !title.trim() || isSubmitting ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSubmit}
            disabled={!title.trim() || isSubmitting}
          >
            {isSubmitting ? t('common.loading') : t('videocall.scheduleMeeting')}
          </button>
        </DetailActions>
      }
    >
      {/* Title */}
      <DetailSection title={t('videocall.meetingTitle')}>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('videocall.meetingTitle')}
          style={inputStyle}
          autoFocus
        />
      </DetailSection>

      {/* Date/Time */}
      <DetailSection title={t('videocall.scheduledFor')}>
        <input
          type="datetime-local"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
          style={inputStyle}
        />
      </DetailSection>

      {/* Duration */}
      <DetailSection title={t('videocall.duration')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="number"
            min="0"
            value={duration}
            onChange={(e) => setDuration(e.target.value ? parseInt(e.target.value) : '')}
            placeholder="30"
            style={{ ...inputStyle, width: '100px' }}
          />
          <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>min</span>
        </div>
      </DetailSection>

      {/* Participants */}
      <DetailSection title={t('videocall.participants')}>
        <input
          type="text"
          value={participantsInput}
          onChange={(e) => setParticipantsInput(e.target.value)}
          placeholder={t('videocall.inviteParticipants')}
          style={inputStyle}
        />
        <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
          {t('videocall.participantsHint')}
        </span>
      </DetailSection>

      {/* Guest Access */}
      <DetailSection title={t('videocall.guestAccess')}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          padding: '10px 12px',
          background: guestAccess ? '#f3e8ff' : 'var(--bg, #f9fafb)',
          borderRadius: '8px',
          border: `1px solid ${guestAccess ? '#7C3AED' : 'var(--line, #e5e7eb)'}`,
        }}>
          <input
            type="checkbox"
            checked={guestAccess}
            onChange={(e) => setGuestAccess(e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#7C3AED' }}
          />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>
              {guestAccess ? t('videocall.guestAccessEnabled') : t('videocall.guestAccessDisabled')}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {t('videocall.guestAccessHint')}
            </div>
          </div>
        </label>
      </DetailSection>
    </DetailPanel>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function VideoCallWidget({
  isOpen = true,
  meetingId,
  meeting,
  onClose,
  onSave,
  onJoinCall,
}: VideoCallWidgetProps) {
  if (!isOpen) return null;

  if (meeting) {
    return (
      <MeetingViewMode
        meeting={meeting}
        onClose={onClose}
        onJoinCall={onJoinCall}
      />
    );
  }

  return <MeetingCreateMode onClose={onClose} onSave={onSave} />;
}

// =============================================================================
// STYLES
// =============================================================================

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--line, #ddd)',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
};

const statCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  background: 'var(--bg, #f9fafb)',
  borderRadius: '10px',
};

const statLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 500,
  color: 'var(--text-muted, #9ca3af)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const statValueStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--text, #1f2937)',
  marginTop: '2px',
};

const closeBtnStyle: React.CSSProperties = {
  padding: '10px 20px',
  border: '1px solid var(--line, #e5e7eb)',
  borderRadius: '8px',
  background: 'white',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
};

const startBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 24px',
  border: 'none',
  borderRadius: '8px',
  background: '#7C3AED',
  color: '#fff',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 600,
};
